/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'underscore',
	'../widgets/livewidget/nls/StringResources',
	'react',
	'react-dom',
	'ca-ui-toolkit'
], function (View, _, stringResources, React, ReactDOM, Toolkit) {
	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.

	const { ToggleSwitch } = Toolkit;
	const { Label } = Toolkit;
	const { NumberInput } = Toolkit;

	const minNumberOfBins = 5;
	const maxNumberOfBins = 100;

	class TitleDiv extends React.Component {
		componentDidUpdate() {
			this.props.setFocusOnNumberInput();
		}
		render() {
			const titleLabel = stringResources.get('autoBinActionText');
			const binCountLabel = stringResources.get('propAutoBinCount');
			const titleId = 'ag-toggle-switch';
			const binCountId = 'ag-number-input';
			const toggle = <ToggleSwitch
				checked={this.props.toggleChecked}
				onChange={this.props.onToggleChange}
				small={true}
				aria-labelledby={titleId}
			/>;
			const numberInput = <NumberInput
				className='numberOfBinsInput'
				value={this.props.numberOfBins}
				max={maxNumberOfBins}
				min={minNumberOfBins}
				hasValidationError={!this.props.validateNumberOfBinsInput(this.props.numberOfBins)}
				onChange={n => this.props.numberOfBinsChanged(n)}
				disabled={!this.props.toggleChecked}
				ref={this.props.numberInputRef}
			/>;
			return (
				<div className="content">
					<div className='selectionRow'>
						<Label
							id={titleId}
							className='selectionlabel'
							label={titleLabel}
						/>
						{toggle}
					</div>
					<div className='selectionRow'>
						<Label
							id={binCountId}
							className='selectionlabel'
							label={binCountLabel}
						/>
						{numberInput}
					</div>
				</div>
			);
		}

	}

	/*
	* Class to render the react flyout components
	*/
	class ReactComponents extends React.Component {
		/*
		* The props hold a state that should be used as this classes state.
		* When the state changes the component gets re-rendered. So when the
		* toggle state or checkbox state changes we update the state to re-render
		* the component while also calling a callback to tell the parent flyout
		* of the event.
		*/
		constructor(props) {
			super(props);
			this.state = _.clone(props.state);
			this.debouncedAutobinTrigger = this.props.getDebouncedAutobinTrigger();
		}
		/*
		* Toggle switch state changed.
		*/
		toggleChanged() {
			const newState = _.extend({}, this.state, { toggleChecked: !this.state.toggleChecked });
			this.setState(newState);
			this.props.onToggleStateChanged(newState);
		}

		validateNumberOfBinsInput(newNumber) {
			if (this.toggleChecked === false) {
				return true;
			}
			var parsed = parseInt(newNumber, 10);
			if (isNaN(parsed)) { return false; }
			if (parsed > maxNumberOfBins || parsed < minNumberOfBins) {
				return false;
			}
			return true;
		}

		numberOfBinsChanged(newNumber) {
			if (this.state.toggleChecked == false) {
				return;
			}
			var parsed = parseInt(newNumber, 10);
			if (isNaN(parsed)) { return; }

			if (this.state.numberOfBins !=  parsed) {
				this.setState({ numberOfBins: parsed });
				if (parsed <= maxNumberOfBins && parsed >= minNumberOfBins) {
					this.debouncedAutobinTrigger(parsed);
				}
			}
			this.props.setFocusOnNumberInput();
		}

		render() {
			return (
				<TitleDiv onToggleChange={() => this.toggleChanged()}
					setFocusOnNumberInput={() => this.props.setFocusOnNumberInput()}
					numberInputRef={this.props.numberInputRef}
					toggleChecked={this.state.toggleChecked}
					numberOfBins={this.state.numberOfBins}
					validateNumberOfBinsInput={this.validateNumberOfBinsInput}
					numberOfBinsChanged={(newNumber) => this.numberOfBinsChanged(newNumber)} />
			);
		}
	}

	/**
	 * @classdesc  Creates an autobin dialog
	 *
	 * @public
	 *
	 * @param {object} options.slot - The data slot
	 * @param {object} options.widget - The widget
	 */

	class AutobinView extends View {
		constructor(...args) {
			super(...args);
			const options = args[0] || {};
			this._iHeight = '212';
			this._iWidth = '290';
			this.height = options.state.height;
			this.width = options.state.width;
			this.defaultNumberOfBins = options.state.defaultNumberOfBins;
			this.autoBinEventHandler = options.actions.apply;
			this.numberInputRef = React.createRef();
			let binning = options.state.binningStateCb();
			if (binning && binning.bins) {
				this.binningState = {
					numberOfBins: binning.bins
				};
			}
		}

		remove() {
			if (this.$el) {
				ReactDOM.unmountComponentAtNode(this.$el[0]);
			}
			this.viewState = null;
			return super.remove();
		}

		render() {
			this.$el
				.addClass('popoverDialogContainer autoBinAction')
				.height(this._iHeight + 'px')
				.width(this._iWidth + 'px');

			var numberOfBins;
			if (this.binningState && this.binningState.numberOfBins) {
				numberOfBins = this.binningState.numberOfBins;
			} else {
				numberOfBins = this.defaultNumberOfBins;
			}

			this.reactComponents = ReactDOM.render(<ReactComponents
				state={{
					'toggleChecked': this.binningState ? true : false,
					'numberOfBins': numberOfBins
				}}
				numberInputRef={this.numberInputRef}
				onToggleStateChanged={(newState) => this.onToggleChange(newState)}
				setFocusOnNumberInput={() => this._setFocusOnNumberInput()}
				getDebouncedAutobinTrigger={() => this.getDebouncedAutobinTrigger()} />,
			this.$el[0]);

			return this;
		}
		/**
		 * @override overrides the setFocus from authoring toolbar class that is invoked from Flyout class
		 * focus will be set in the input if the auto group is in enabled state (see _setFocusOnNumberInput) 
		 */
		setFocus() {
			this._setFocusOnNumberInput();
		}

		onToggleChange(reactState) {
			if (this.binningState) {
				this.binningState = null;
			} else {
				this.binningState = {
					numberOfBins: reactState.numberOfBins
				};
			}
			if (this.binningState) {
				this.autoBinEventHandler(this.binningState);
			} else {
				this.autoBinEventHandler(null);
			}
		}

		getDebouncedAutobinTrigger() {
			return _.debounce((numberOfBins) => this.numberOfBinsChanged(numberOfBins), 500);
		}

		numberOfBinsChanged(numberOfBins) {
			if (this.binningState) {
				this.binningState.numberOfBins = numberOfBins;
				this.autoBinEventHandler(this.binningState);
			}
		}

		/**
		 * calls focus from toolkit NumberInput component if the auto-group is enabled
		 */
		_setFocusOnNumberInput() {
			if (this.binningState) {
				this.numberInputRef && this.numberInputRef.current.focus();
			}
		}
	}

	return AutobinView;
});
